/**
 * **A client's month**, for the two task kinds a single week cannot answer: a client who stopped
 * training (`lapsedActivity`) and a client with nothing booked ahead (`noUpcomingSessions`).
 *
 * Owner, 24 September 2026: a seven-day strip under "Brak zaplanowanych treningów" always reads
 * "nothing", which says nothing. A month does: the weeks behind (trained, missed, with the session's
 * name), today, and — for the empty calendar — the weeks ahead where the plan's own days wait as
 * GHOSTS, days the plan prescribes that nobody has booked.
 *
 * ── Rows are whole Monday weeks, and each row carries its own tally ──────────────────────────
 * "3 z 3" beside a week is the reading the trainer makes of a row anyway, and making them count it
 * is what the canon's right-hand column exists to spare. The tally counts SCHEDULED work on the
 * calendar entity's own rule (`occurrenceIsScheduledWork`), the same as the week strip's
 * attendance run: withdrawn work stands in neither side.
 *
 * ── A ghost never covers a real occurrence ───────────────────────────────────────────────────
 * The plan's slots come from the execution summary, the bookings from the schedule; the two
 * describe the same sessions where a slot IS booked. A day holding a real occurrence draws that
 * occurrence, and a ghost is only drawn on a day the schedule left empty — otherwise a missed
 * session would read as "planned, no date" and a finished one could be hatched over.
 *
 * ── Nothing dated anywhere is a SENTENCE, not an empty month ─────────────────────────────────
 * The week strip's rule, for the same reason: a self-paced client is never given dates
 * (self-paced-plan R15), and twenty-eight blank squares would say they were given nothing to do.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import {
  occurrenceIsScheduledWork,
  occurrencePlacementDay,
  occurrenceStatusOf,
  occurrenceWasDone,
  occurrenceWasMissed,
} from '~/entities/calendar-event';
import { addDays, toIsoDate } from '~/shared/lib/calendarDate';

import { mondayOfDay, type ClientWeekEvent } from './clientWeek';

const DAYS_PER_WEEK = 7;

/** A day the plan prescribes and nobody booked — a slot of the execution summary, not a booking. */
export interface PlanGhost {
  readonly day: string;
  /** The plan day's name, where it has one. */
  readonly title: string;
}

/** What one square IS. `rest` is a day with nothing owed; `ahead` a future day with nothing on it. */
export type MonthCellKind = 'done' | 'missed' | 'planned' | 'ghost' | 'rest' | 'ahead';

export interface MonthCell {
  readonly day: string;
  readonly kind: MonthCellKind;
  /** The session's or the plan day's name; `''` where nothing named it. */
  readonly title: string;
  readonly isToday: boolean;
  /** The last day this client finished a session — the canon rings it on a lapse. */
  readonly isLastTrained: boolean;
}

export interface MonthRow {
  /** The row's Monday. */
  readonly startDay: string;
  readonly cells: readonly MonthCell[];
  /** Finished sessions in the row. */
  readonly done: number;
  /**
   * Work the row owes — scheduled occurrences, and the plan's un-booked days ahead of today — the
   * tally's denominator. `0` for a row that owed nothing.
   */
  readonly owed: number;
  readonly isCurrent: boolean;
  /** The whole row lies after today. Its tally is not a verdict and is drawn without one. */
  readonly isAhead: boolean;
}

export type MonthCalendar =
  | {
      readonly kind: 'month';
      readonly rows: readonly MonthRow[];
      /** The last finished session in the window, or `null` where there was none in it. */
      readonly lastTrainedDay: string | null;
      /** Whether any ghost was placed — the legend names the hatching only when it is on screen. */
      readonly hasGhosts: boolean;
    }
  | { readonly kind: 'undated' };

export interface MonthSpan {
  /** Whole weeks drawn BEFORE the current one. */
  readonly weeksBack: number;
  /** Whole weeks drawn AFTER it. `0` for a lapse, which is about what has been. */
  readonly weeksAhead: number;
}

/** A lapse looks back: three weeks behind and this one. */
export const LAPSE_MONTH: MonthSpan = { weeksBack: 3, weeksAhead: 0 };
/** An empty calendar looks both ways: two behind, this one, and the one ahead the plan waits in. */
export const EMPTY_CALENDAR_MONTH: MonthSpan = { weeksBack: 2, weeksAhead: 1 };
/**
 * The Przegląd's card holds one height, and under an empty calendar it also carries „Przesuń resztę
 * planu": this week and the one ahead the plan waits in — the two the task is about.
 */
export const EMPTY_CALENDAR_FORTNIGHT: MonthSpan = { weeksBack: 0, weeksAhead: 1 };

/** The first and last civil day the month needs. Hand this to `scheduleWindow`. */
export function monthSpanDays(
  todayIso: string,
  span: MonthSpan,
): { readonly first: string; readonly last: string } {
  const monday = mondayOfDay(todayIso);
  if (monday === null) return { first: todayIso, last: todayIso };
  return {
    first: toIsoDate(addDays(monday, -DAYS_PER_WEEK * span.weeksBack)),
    last: toIsoDate(addDays(monday, DAYS_PER_WEEK * (span.weeksAhead + 1) - 1)),
  };
}

export function monthCalendar(
  events: readonly ClientWeekEvent[],
  todayIso: string,
  span: MonthSpan,
  ghosts: readonly PlanGhost[] = [],
): MonthCalendar {
  const currentMonday = mondayOfDay(todayIso);
  if (currentMonday === null) return { kind: 'undated' };

  const byDay = new Map<string, ClientWeekEvent[]>();
  for (const event of events) {
    // The reader's own clock: the same day main placed it on before the zone became explicit.
    const day = occurrencePlacementDay(event, null);
    if (day === null) continue;
    const bucket = byDay.get(day);
    if (bucket === undefined) byDay.set(day, [event]);
    else bucket.push(event);
  }
  const ghostByDay = new Map<string, PlanGhost>();
  for (const ghost of ghosts) {
    if (ghost.day > todayIso && !ghostByDay.has(ghost.day)) ghostByDay.set(ghost.day, ghost);
  }
  if (byDay.size === 0 && ghostByDay.size === 0) return { kind: 'undated' };

  let lastTrainedDay: string | null = null;
  for (const [day, bucket] of byDay) {
    if (day > todayIso) continue;
    if (bucket.some((event) => occurrenceWasDone(occurrenceStatusOf(event)))) {
      if (lastTrainedDay === null || day > lastTrainedDay) lastTrainedDay = day;
    }
  }

  const currentStart = toIsoDate(currentMonday);
  const rows: MonthRow[] = [];
  let hasGhosts = false;
  for (let offset = -span.weeksBack; offset <= span.weeksAhead; offset += 1) {
    const monday = addDays(currentMonday, DAYS_PER_WEEK * offset);
    const cells: MonthCell[] = [];
    let done = 0;
    let owed = 0;
    for (let step = 0; step < DAYS_PER_WEEK; step += 1) {
      const day = toIsoDate(addDays(monday, step));
      const bucket = byDay.get(day) ?? [];
      for (const event of bucket) {
        const key = occurrenceStatusOf(event);
        if (!occurrenceIsScheduledWork(key)) continue;
        owed += 1;
        if (occurrenceWasDone(key)) done += 1;
      }
      const cell = cellOf(day, bucket, ghostByDay.get(day) ?? null, todayIso);
      if (cell.kind === 'ghost') {
        hasGhosts = true;
        // A plan day waiting for a booking is work the plan still owes — "0 z 3" ahead, not "0 z 0".
        owed += 1;
      }
      cells.push({ ...cell, isLastTrained: day === lastTrainedDay });
    }
    const startDay = toIsoDate(monday);
    rows.push({
      startDay,
      cells,
      done,
      owed,
      isCurrent: startDay === currentStart,
      isAhead: startDay > currentStart,
    });
  }

  return { kind: 'month', rows, lastTrainedDay, hasGhosts };
}

function cellOf(
  day: string,
  bucket: readonly ClientWeekEvent[],
  ghost: PlanGhost | null,
  todayIso: string,
): Omit<MonthCell, 'isLastTrained'> {
  const isToday = day === todayIso;
  const done = bucket.find((event) => occurrenceWasDone(occurrenceStatusOf(event)));
  if (done !== undefined) return { day, kind: 'done', title: done.sessionName ?? '', isToday };
  const missed = bucket.find((event) => occurrenceWasMissed(occurrenceStatusOf(event)));
  if (missed !== undefined) return { day, kind: 'missed', title: missed.sessionName ?? '', isToday };
  const planned = bucket.find((event) => {
    const key = occurrenceStatusOf(event);
    return key === 'scheduled' || key === 'live';
  });
  if (planned !== undefined) {
    return { day, kind: 'planned', title: planned.sessionName ?? '', isToday };
  }
  // A real occurrence always wins over a ghost; a ghost only fills a day the schedule left empty.
  if (ghost !== null) return { day, kind: 'ghost', title: ghost.title, isToday };
  return { day, kind: day > todayIso ? 'ahead' : 'rest', title: '', isToday };
}
